<template>
  <div
    class="spec"
    :class="[
      `spec--${theme}`,
      { 'spec--comparison': isComparisonTool, 'spec--vertical': isVerticalLayout },
    ]"
  >
    <div class="spec__spider-graph">
      <div class="spec__spider-graph-inner">
        <Spider
          v-for="(specs, keys, index) in spiderOrdered"
          :key="index"
          :specs="specs"
          :theme="theme"
          :layout="spiderLayout"
          :title="Object.keys(spiderOrdered)[index]"
          :type="productType"
        />
      </div>
    </div>
    <div v-if="isSeparatorVisible" class="spec__separator"></div>

    <!-- Shoes spec -->
    <div v-if="productType === SPECS_PRODUCT_TYPES.shoes" class="spec__facts spec__facts--shoes">
      <div class="spec__facts-inner">
        <div class="spec__facts-box" v-if="isMqLarge">
          <div class="spec__facts-title">{{ $t('best_for') }}</div>
          <div class="spec__facts-description spec__facts-description--copy">
            {{ spec.bestFor }}
          </div>
        </div>
        <div class="spec__facts-box">
          <div class="spec__facts-title">{{ $t('weight') }}</div>
          <div
            class="spec__description-container"
            :class="{ 'spec__description-container--oz-first': isCountryUnitedStates }"
          >
            <div
              class="spec__facts-description"
              :class="{ 'spec__facts-description--large': !isCountryUnitedStates }"
            >
              {{ weight }}
              <span class="spec__unit" :class="{ 'spec__unit--large': !isCountryUnitedStates }">
                {{ SPECS_MEASURES.default.unitWeight }}
              </span>
            </div>
            <div
              class="spec__facts-description"
              :class="{ 'spec__facts-description--large': isCountryUnitedStates }"
            >
              {{ (weight * SPECS_MEASURES.us.conversionFactorWeight).toFixed(2) }}
              <span class="spec__unit" :class="{ 'spec__unit--large': isCountryUnitedStates }">
                {{ SPECS_MEASURES.us.unitWeight }}
              </span>
            </div>
          </div>
        </div>

        <div class="spec__facts-box">
          <div class="spec__facts-title">{{ $t('drop') }}</div>
          <div class="spec__description-container">
            <div class="spec__facts-description spec__facts-description--large">
              {{ this.spec.heelToeDrop }}
              <span class="spec__unit spec__unit--large">{{
                SPECS_MEASURES.default.unitLength
              }}</span>
            </div>
          </div>
        </div>

        <div class="spec__facts-box" v-if="!isMqLarge">
          <div class="spec__facts-title">{{ $t('best_for') }}</div>
          <div class="spec__facts-description spec__facts-description--copy">
            {{ spec.bestFor }}
          </div>
        </div>

        <div class="spec__facts-box" v-if="spec.waterproof">
          <div class="spec__facts-title" v-show="spec.waterproof">
            {{ $t('waterproof') }}
          </div>
          <div class="spec__facts-description spec__facts-description--large">
            <span class="spec__icon icon-condition-rain"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Apparel Specs -->
    <div class="spec__facts spec__facts--apparel" v-else>
      <div class="spec__facts-inner">
        <div class="spec__facts-box">
          <div class="spec__facts-title">{{ $t('best_for') }}</div>
          <div class="spec__facts-description spec__facts-description--copy">
            {{ spec.bestFor }}
          </div>
        </div>
        <div class="spec__facts-box">
          <div class="spec__facts-title">{{ $t('weight') }}</div>
          <div
            class="spec__description-container"
            :class="{ 'spec__description-container--oz-first': isCountryUnitedStates }"
          >
            <div
              class="spec__facts-description"
              :class="{ 'spec__facts-description--large': !isCountryUnitedStates }"
            >
              {{ weight }}
              <span class="spec__unit" :class="{ 'spec__unit--large': !isCountryUnitedStates }">
                {{ SPECS_MEASURES.default.unitWeight }}
              </span>
            </div>
            <div
              class="spec__facts-description"
              :class="{ 'spec__facts-description--large': isCountryUnitedStates }"
            >
              {{ (weight * SPECS_MEASURES.us.conversionFactorWeight).toFixed(2) }}
              <span class="spec__unit" :class="{ 'spec__unit--large': isCountryUnitedStates }">
                {{ SPECS_MEASURES.us.unitWeight }}
              </span>
            </div>
          </div>
        </div>

        <div class="spec__facts-box">
          <div class="spec__facts-title">{{ $t('conditions') }}</div>
          <div class="spec__facts-description">
            <template v-for="(condition, key, index) in SPECS_CONDITIONS">
              <div v-if="spec.conditions === condition" :key="index" class="spec__condition">
                <div :class="`spec__icon icon-condition-${condition}`"></div>
                <div class="spec__icon-description">
                  {{ $t(`apparel.${condition}`) }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { GENDERS } from '@/stores/product'
import {
  SPECS_PRODUCT_TYPES,
  SPECS_MEASURES,
  SPECS_CONDITIONS,
  SPECS_DEFAULT_ORDER,
} from '@/components/_shared/Specs/constants'
import { isEmpty } from '@/helpers/object.js'
import { LAYOUTS } from '@/components/_shared/Specs/Spider/Spider.vue'
import Spider from '@/components/_shared/Specs/Spider/Spider.vue'

export default {
  name: 'Spec',
  components: { Spider },

  props: {
    isComparisonTool: {
      type: Boolean,
      default: false,
    },

    isSeparatorVisible: {
      type: Boolean,
      default: true,
    },

    isVerticalLayout: {
      type: Boolean,
      default: false,
    },

    productType: {
      type: String,
      default: SPECS_PRODUCT_TYPES.shoes,
    },

    spec: {
      type: Object,
      required: true,
    },

    selectedGender: {
      type: String,
      default: GENDERS.mens,
      validator: value => Object.values(GENDERS).includes(value),
    },

    theme: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      SPECS_CONDITIONS,
      SPECS_MEASURES,
      SPECS_PRODUCT_TYPES,
      SPECS_DEFAULT_ORDER,
      spiderOrdered: {},
    }
  },

  computed: {
    ...mapGetters({
      country: 'page/country',
      paths: 'order/paths',
      isMqLarge: 'viewport/isMqLarge',
    }),

    weight() {
      return this.selectedGender === GENDERS.mens ? this.spec.weightMen : this.spec.weightWomen
    },

    isCountryUnitedStates() {
      return this.country === 'us'
    },

    spiderLayout() {
      return this.isMqLarge ? LAYOUTS.horizontal : LAYOUTS.vertical
    },
  },

  watch: {
    spec() {
      this.orderSpiderValues()
    },
  },

  created() {
    if (!isEmpty(this.spec)) this.orderSpiderValues()
  },

  methods: {
    orderSpiderValues() {
      Object.keys(SPECS_DEFAULT_ORDER[this.productType]).forEach(spec => {
        if (Object.keys(this.spec.spider).includes(spec)) {
          this.spiderOrdered[spec] = []
          Object.keys(SPECS_DEFAULT_ORDER[this.productType][spec]).forEach(value => {
            const position = SPECS_DEFAULT_ORDER[this.productType][spec][value]
            this.spiderOrdered[spec][position] = [value, this.spec.spider[spec][value]]
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Spec';
</style>

<i18n src="@/locales/spec.json">
</i18n>
